/** Lo que devuelve la API de tesorería. */

export type Banco = {
  id: number
  codigo: string | null
  nombre: string
  activo: boolean
  /** Cuántas cuentas cuelgan. Deja avisar antes de archivar. */
  cuentas: number
}

/** Qué clase de sitio guarda el dinero, y por tanto qué datos lleva. */
export type ClaseCuenta =
  | "BANCO_NACIONAL"
  | "BANCO_INTERNACIONAL"
  | "EXCHANGE"
  | "CRIPTO"
  | "EFECTIVO"

export type Cuenta = {
  id: number
  nombre: string
  clase: ClaseCuenta
  banco_id: number | null
  /** El nombre del banco, ya resuelto por el servidor. */
  banco: string | null
  /**
   * Los cuatro últimos caracteres del número, lo único de él que viaja: distingue dos
   * cuentas del mismo banco. `null` si el número no llega a cuatro.
   */
  ultimos4: string | null
  /** El banco de fuera, que no sale de catálogo. */
  banco_nombre: string | null
  titular: string | null
  swift: string | null
  pais: string | null
  /** La red de una billetera y la ubicación del efectivo NO son secretos: sin ellas
   *  no se puede pagar ni saber dónde está la caja. */
  red: string | null
  ubicacion: string | null
  /** Si pide PIN para abrirse. El hash nunca sale del servidor. */
  tiene_pin: boolean
  /**
   * Si la cuenta puede quedar en NEGATIVO. Por omisión no: un saldo negativo casi
   * siempre es un movimiento que falta. Se marca cuenta por cuenta, para las que
   * adelantan gastos y se reponen después.
   */
  permite_sobregiro: boolean
  /** Si cada pago registra el ingreso que lo fondea: la cuenta conducto. */
  fondeo_automatico?: boolean
  /** (RM-5) El fondo fijo si la cuenta es caja chica. */
  fondo_fijo?: string | null
  divisa_id: number
  tipo: string | null
  activo: boolean
}

export type Divisa = {
  id: number
  codigo: string
  nombre: string
  simbolo: string
  es_referencia: boolean
  activo: boolean
}

export type TasaVigente = {
  codigo: string
  nombre: string
  simbolo: string
  es_referencia: boolean
  tasa: string | null
  fuente: string | null
  fecha_valor: string | null
}

/**
 * Lo que enseña la ficha de una cuenta encima del extracto.
 *
 * ⚠ **Entradas y salidas son de TODA la historia, no del extracto que se ve.** El
 * extracto va con tope; sumar lo que se ve daría cifras que cambian con el tope y no
 * con el dinero. Las anuladas no cuentan en ninguna de las tres.
 */
export type ResumenCuenta = {
  cuenta_id: number
  saldo: string
  entradas: string
  salidas: string
  movimientos: number
  ultimo_movimiento: string | null
}

export type Movimiento = {
  id: number
  cuenta_id: number
  tipo: string
  clase_fondo: string
  expediente_id: number | null
  monto: string
  divisa_id: number
  tasa_cambio_id: number | null
  referencia: string | null
  concepto: string
  anulado: boolean
  motivo_anulacion: string | null
  /** El día en que el banco movió el dinero, `AAAA-MM-DD`. */
  fecha_valor: string
  /** Cuándo se tecleó la fila. No es lo mismo, y los dos quedan. */
  creado_en: string
  /** Lo que respalda el movimiento. El contenido va por su propia ruta. */
  soportes: { id: number; nombre_original: string; mime: string; bytes: number }[]
  /**
   * Si se puede corregir su concepto desde la cuenta. Cosmético, como todo lo que
   * decide qué botón se ve: el servidor lo vuelve a comprobar al intentarlo.
   */
  editable: boolean
}

/**
 * Los tipos de cuenta que la agencia usa.
 *
 * ⚠ **No todo lo que guarda dinero es un banco.** El efectivo en caja y una
 * billetera de USDT son cuentas a efectos del ledger —tienen saldo y movimientos—
 * pero no tienen banco ni número. Por eso `banco_id` es opcional: forzar un banco
 * en una caja chica obligaría a inventarse uno.
 */
export const TIPOS_CUENTA = ["CORRIENTE", "AHORRO", "CUSTODIA", "EFECTIVO", "BILLETERA"] as const

export type TipoCuenta = (typeof TIPOS_CUENTA)[number]

/** De dónde vino la tasa. Es lo que distingue el BCV del mercado P2P. */
export const TONO_FUENTE: Record<string, "exito" | "info" | "neutro"> = {
  BCV: "exito",
  BINANCE: "info",
  MANUAL: "neutro",
}
